/* eslint angular/no-private-call:0 */

// TODO: remove the option for templateUrl for modals (require a raw template)

import modalWindowTemplate from './modal-window.tpl.html';

/**
 * A helper, internal data structure that acts as a map but also allows getting / removing
 * elements in the LIFO order
 */
const $$stackedMap = {
  createNew: function () {
    const stack = [];

    return {
      add: function (key, value) {
        stack.push({
          key: key,
          value: value,
        });
      },
      get: function (key) {
        for (let i = 0; i < stack.length; i++) {
          if (key === stack[i].key) {
            return stack[i];
          }
        }
      },
      keys: function () {
        const keys = [];
        for (let i = 0; i < stack.length; i++) {
          keys.push(stack[i].key);
        }
        return keys;
      },
      top: function () {
        return stack[stack.length - 1];
      },
      remove: function (key) {
        let idx = -1;
        for (let i = 0; i < stack.length; i++) {
          if (key === stack[i].key) {
            idx = i;
            break;
          }
        }
        return stack.splice(idx, 1)[0];
      },
      length: function () {
        return stack.length;
      },
    };
  },
};

/**
 * A helper, internal data structure that stores all references attached to key
 */
const $$multiMap = {
  createNew: function () {
    const map = {};

    return {
      get: function (key) {
        return map[key];
      },
      hasKey: function (key) {
        return !!map[key];
      },
      keys: function () {
        return Object.keys(map);
      },
      put: function (key, value) {
        if (!map[key]) {
          map[key] = [];
        }

        map[key].push(value);
      },
      remove: function (key, value) {
        let values = map[key];

        if (!values) {
          return;
        }

        const idx = values.indexOf(value);

        if (idx !== -1) {
          values.splice(idx, 1);
        }

        if (!values.length) {
          delete map[key];
        }
      },
    };
  },
};

export const modalComponentModule = angular
  .module('Q.components.modal', [])
  /**
   * Pluggable resolve mechanism for the modal resolve resolution
   * Supports UI Router's $resolve service
   */
  .provider('$uibResolve', function () {
    this.$get = function ($injector, $q) {
      return {
        resolve: function (invocables) {
          const promises = [];

          angular.forEach(invocables, function (value) {
            if (angular.isFunction(value) || angular.isArray(value)) {
              promises.push($q.resolve($injector.invoke(value)));
            } else if (angular.isString(value)) {
              promises.push($q.resolve($injector.get(value)));
            } else {
              promises.push($q.resolve(value));
            }
          });

          return $q.all(promises).then(function (resolves) {
            const resolveObj = {};
            let resolveIter = 0;
            angular.forEach(invocables, function (value, key) {
              resolveObj[key] = resolves[resolveIter++];
            });

            return resolveObj;
          });
        },
      };
    };
  })

  /**
   * A helper directive for the $modal service. It creates a backdrop element.
   */
  .directive('uibModalBackdrop', function ($animate, $injector, $modalStack) {
    return {
      restrict: 'A',
      compile: function (tElement, tAttrs) {
        tElement.addClass(tAttrs.backdropClass);
        return linkFn;
      },
    };

    function linkFn(scope, element, attrs) {
      if (attrs.modalInClass) {
        $animate.addClass(element, attrs.modalInClass);

        scope.$on($modalStack.NOW_CLOSING_EVENT, function (e, setIsAsync) {
          const done = setIsAsync();
          if (scope.modalOptions.animation) {
            $animate.removeClass(element, attrs.modalInClass).then(done);
          } else {
            done();
          }
        });
      }
    }
  })

  .directive(
    'uibModalWindow',
    function ($modalStack, $q, $animateCss, $document) {
      return {
        scope: {
          index: '@',
        },
        restrict: 'A',
        transclude: true,
        template: function (tElement) {
          return tElement.data('window-template') || modalWindowTemplate;
        },
        link: function (scope, element, attrs) {
          element.addClass(attrs.windowTopClass || '');
          scope.size = attrs.size;
          scope.allowDarkMode = !!attrs.allowDarkMode;

          scope.close = function (evt) {
            const modal = $modalStack.getTop();
            if (
              modal &&
              modal.value.backdrop &&
              modal.value.backdrop !== 'static' &&
              evt.target === evt.currentTarget
            ) {
              evt.preventDefault();
              evt.stopPropagation();
              $modalStack.dismiss(modal.key, 'backdrop click');
            }
          };

          // moved from template to fix issue #2280
          element.on('click', scope.close);

          // This property is only added to the scope for the purpose of detecting when this directive is rendered.
          // We can detect that by using this property in the template associated with this directive and then use
          // {@link Attribute#$observe} on it. For more details please see {@link TableColumnResize}.
          scope.$isRendered = true;

          // Deferred object that will be resolved when this modal is rendered.
          const modalRenderDeferObj = $q.defer();
          // Resolve render promise post-digest
          scope.$$postDigest(function () {
            modalRenderDeferObj.resolve();
          });

          modalRenderDeferObj.promise.then(function () {
            let animationPromise = null;

            if (attrs.modalInClass) {
              animationPromise = $animateCss(element, {
                addClass: attrs.modalInClass,
              }).start();

              scope.$on(
                $modalStack.NOW_CLOSING_EVENT,
                function (e, setIsAsync) {
                  const done = setIsAsync();
                  $animateCss(element, {
                    removeClass: attrs.modalInClass,
                  })
                    .start()
                    .then(done);
                },
              );
            }

            $q.when(animationPromise).then(function () {
              // Notify {@link $modalStack} that modal is rendered.
              const modal = $modalStack.getTop();
              if (modal) {
                $modalStack.modalRendered(modal.key);
              }

              /**
               * If something within the freshly-opened modal already has focus (perhaps via a
               * directive that causes focus) then there's no need to try to focus anything.
               */
              if (
                !(
                  $document[0].activeElement &&
                  element[0].contains($document[0].activeElement)
                )
              ) {
                const inputWithAutofocus =
                  element[0].querySelector('[autofocus]');
                /**
                 * Auto-focusing of a freshly-opened modal element causes any child elements
                 * with the autofocus attribute to lose focus. This is an issue on touch
                 * based devices which will show and then hide the onscreen keyboard.
                 * Attempts to refocus the autofocus element via JavaScript will not reopen
                 * the onscreen keyboard. Fixed by updated the focusing logic to only autofocus
                 * the modal element if the modal does not contain an autofocus element.
                 */
                if (inputWithAutofocus) {
                  inputWithAutofocus.focus();
                } else {
                  element[0].focus();
                }
              }
            });
          });
        },
      };
    },
  )

  .directive('uibModalAnimationClass', function () {
    return {
      compile: function (tElement, tAttrs) {
        if (tAttrs.modalAnimation) {
          tElement.addClass(tAttrs.uibModalAnimationClass);
        }
      },
    };
  })

  .directive('uibModalTransclude', function ($animate) {
    return {
      link: function (scope, element, attrs, controller, transclude) {
        transclude(scope.$parent, function (clone) {
          element.empty();
          $animate.enter(clone, element);
        });
      },
    };
  })

  .factory(
    '$modalStack',
    function ($animate, $animateCss, $document, $compile, $rootScope, $q) {
      const OPENED_MODAL_CLASS = 'modal-open';

      let backdropDomEl;
      let backdropScope;
      const openedWindows = $$stackedMap.createNew();
      const openedClasses = $$multiMap.createNew();
      const $modalStack = {
        NOW_CLOSING_EVENT: 'modal.stack.now-closing',
      };
      let topModalIndex = 0;
      let previousTopOpenedModal = null;
      const ARIA_HIDDEN_ATTRIBUTE_NAME =
        'data-bootstrap-modal-aria-hidden-count';

      //Modal focus behavior
      const tabbableSelector =
        "a[href], area[href], input:not([disabled]):not([tabindex='-1']), " +
        "button:not([disabled]):not([tabindex='-1']),select:not([disabled]):not([tabindex='-1']), textarea:not([disabled]):not([tabindex='-1']), " +
        "iframe, object, embed, *[tabindex]:not([tabindex='-1']), *[contenteditable=true]";
      let scrollbarPadding;
      const SNAKE_CASE_REGEXP = /[A-Z]/g;

      // TODO: extract into common dependency with tooltip
      function snakeCase(name) {
        const separator = '-';
        return name.replace(SNAKE_CASE_REGEXP, function (letter, pos) {
          return (pos ? separator : '') + letter.toLowerCase();
        });
      }

      function isVisible(element) {
        return !!(
          element.offsetWidth ||
          element.offsetHeight ||
          element.getClientRects().length
        );
      }

      function backdropIndex() {
        let topBackdropIndex = -1;
        const opened = openedWindows.keys();
        for (let i = 0; i < opened.length; i++) {
          if (openedWindows.get(opened[i]).value.backdrop) {
            topBackdropIndex = i;
          }
        }

        // If any backdrop exist, ensure that it's index is always
        // right below the top modal
        if (topBackdropIndex > -1 && topBackdropIndex < topModalIndex) {
          topBackdropIndex = topModalIndex;
        }
        return topBackdropIndex;
      }

      $rootScope.$watch(backdropIndex, function (newBackdropIndex) {
        if (backdropScope) {
          backdropScope.index = newBackdropIndex;
        }
      });

      function removeModalWindow(modalInstance, elementToReceiveFocus) {
        const modalWindow = openedWindows.get(modalInstance).value;
        const appendToElement = modalWindow.appendTo;

        //clean up the stack
        openedWindows.remove(modalInstance);
        previousTopOpenedModal = openedWindows.top();
        if (previousTopOpenedModal) {
          topModalIndex = parseInt(
            previousTopOpenedModal.value.modalDomEl.attr('index'),
            10,
          );
        }

        removeAfterAnimate(
          modalWindow.modalDomEl,
          modalWindow.modalScope,
          function () {
            const modalBodyClass =
              modalWindow.openedClass || OPENED_MODAL_CLASS;
            openedClasses.remove(modalBodyClass, modalInstance);
            let areAnyOpen = openedClasses.hasKey(modalBodyClass);
            appendToElement.toggleClass(modalBodyClass, areAnyOpen);
            if (
              !areAnyOpen &&
              scrollbarPadding &&
              scrollbarPadding.heightOverflow &&
              scrollbarPadding.scrollbarWidth
            ) {
              if (scrollbarPadding.originalRight) {
                appendToElement.css({
                  paddingRight: scrollbarPadding.originalRight + 'px',
                });
              } else {
                appendToElement.css({ paddingRight: '' });
              }
              scrollbarPadding = null;
            }
            toggleTopWindowClass(true);
          },
          modalWindow.closedDeferred,
        );
        checkRemoveBackdrop();

        //move focus to specified element if available, or else to body
        if (elementToReceiveFocus && elementToReceiveFocus.focus) {
          elementToReceiveFocus.focus();
        } else if (appendToElement.focus) {
          appendToElement.focus();
        }
      }

      // Add or remove "windowTopClass" from the top window in the stack
      function toggleTopWindowClass(toggleSwitch) {
        let modalWindow;

        if (openedWindows.length() > 0) {
          modalWindow = openedWindows.top().value;
          modalWindow.modalDomEl.toggleClass(
            modalWindow.windowTopClass || '',
            toggleSwitch,
          );
        }
      }

      function checkRemoveBackdrop() {
        //remove backdrop if no longer needed
        if (backdropDomEl && backdropIndex() === -1) {
          removeAfterAnimate(backdropDomEl, backdropScope);
          backdropDomEl = undefined;
          backdropScope = undefined;
        }
      }

      function removeAfterAnimate(domEl, scope, done, closedDeferred) {
        let asyncDeferred;
        let asyncPromise = null;
        const setIsAsync = function () {
          if (!asyncDeferred) {
            asyncDeferred = $q.defer();
            asyncPromise = asyncDeferred.promise;
          }

          return function asyncDone() {
            asyncDeferred.resolve();
          };
        };
        scope.$broadcast($modalStack.NOW_CLOSING_EVENT, setIsAsync);

        // Note that it's intentional that asyncPromise might be null.
        // That's when setIsAsync has not been called during the
        // NOW_CLOSING_EVENT broadcast.
        return $q.when(asyncPromise).then(afterAnimating);

        function afterAnimating() {
          if (afterAnimating.done) {
            return;
          }
          afterAnimating.done = true;

          $animate.leave(domEl).then(function () {
            if (done) {
              done();
            }

            domEl.remove();
            if (closedDeferred) {
              closedDeferred.resolve();
            }
          });

          scope.$destroy();
        }
      }

      $document.on('keydown', keydownListener);

      $rootScope.$on('$destroy', function () {
        $document.off('keydown', keydownListener);
      });

      function keydownListener(evt) {
        if (evt.isDefaultPrevented()) {
          return evt;
        }

        const modal = openedWindows.top();
        if (modal) {
          switch (evt.which) {
            case 27: {
              if (modal.value.keyboard) {
                evt.preventDefault();
                $rootScope.$apply(function () {
                  $modalStack.dismiss(modal.key, 'escape key press');
                });
              }
              break;
            }
            case 9: {
              const list = $modalStack.loadFocusElementList(modal);
              let focusChanged = false;
              if (evt.shiftKey) {
                if (
                  $modalStack.isFocusInFirstItem(evt, list) ||
                  $modalStack.isModalFocused(evt, modal)
                ) {
                  focusChanged = $modalStack.focusLastFocusableElement(list);
                }
              } else {
                if ($modalStack.isFocusInLastItem(evt, list)) {
                  focusChanged = $modalStack.focusFirstFocusableElement(list);
                }
              }

              if (focusChanged) {
                evt.preventDefault();
                evt.stopPropagation();
              }

              break;
            }
          }
        }
      }

      $modalStack.open = function (modalInstance, modal) {
        const modalOpener = $document[0].activeElement;
        const modalBodyClass = modal.openedClass || OPENED_MODAL_CLASS;

        toggleTopWindowClass(false);

        // Store the current top first, to determine what index we ought to use
        // for the current top modal
        previousTopOpenedModal = openedWindows.top();

        openedWindows.add(modalInstance, {
          deferred: modal.deferred,
          renderDeferred: modal.renderDeferred,
          closedDeferred: modal.closedDeferred,
          modalScope: modal.scope,
          backdrop: modal.backdrop,
          keyboard: modal.keyboard,
          openedClass: modal.openedClass,
          windowTopClass: modal.windowTopClass,
          animation: modal.animation,
          appendTo: modal.appendTo,
        });

        openedClasses.put(modalBodyClass, modalInstance);

        const appendToElement = modal.appendTo;
        const currBackdropIndex = backdropIndex();

        if (currBackdropIndex >= 0 && !backdropDomEl) {
          backdropScope = $rootScope.$new(true);
          backdropScope.modalOptions = modal;
          backdropScope.index = currBackdropIndex;
          backdropDomEl = angular.element(
            '<div uib-modal-backdrop="modal-backdrop"></div>',
          );
          backdropDomEl.attr({
            class: 'modal-backdrop',
            'ng-style': "{'z-index': 1040 + (index && 1 || 0) + index*10}",
            'uib-modal-animation-class': 'fade',
            'modal-in-class': 'in',
          });
          if (modal.backdropClass) {
            backdropDomEl.addClass(modal.backdropClass);
          }

          if (modal.animation) {
            backdropDomEl.attr('modal-animation', 'true');
          }
          $compile(backdropDomEl)(backdropScope);
          const animatedBackdropEl = backdropDomEl;
          $animate.enter(animatedBackdropEl, appendToElement).then(() => {
            if (animatedBackdropEl !== backdropDomEl) {
              // backdrop got added after everything was closed, so just remove it immediately
              animatedBackdropEl.remove();
            }
          });
        }

        let content;
        if (modal.component) {
          content = document.createElement(snakeCase(modal.component.name));
          content = angular.element(content);
          content.attr({
            resolve: '$resolve',
            'modal-instance': '$modalInstance',
            close: '$close($value)',
            dismiss: '$dismiss($value)',
          });
        } else {
          content = modal.content;
        }

        // Set the top modal index based on the index of the previous top modal
        topModalIndex = previousTopOpenedModal
          ? parseInt(
              previousTopOpenedModal.value.modalDomEl.attr('index'),
              10,
            ) + 1
          : 0;
        const angularDomEl = angular.element(
          '<div uib-modal-window="modal-window"></div>',
        );
        angularDomEl
          .attr({
            class: 'modal',
            'window-top-class': modal.windowTopClass,
            'allow-dark-mode': modal.allowDarkMode || '',
            role: 'dialog',
            'aria-labelledby': modal.ariaLabelledBy,
            'aria-describedby': modal.ariaDescribedBy,
            size: modal.size,
            index: topModalIndex,
            animate: 'animate',
            'ng-style':
              "{'z-index': 1050 + $$topModalIndex*10, display: 'block'}",
            tabindex: -1,
            'uib-modal-animation-class': 'fade',
            'modal-in-class': 'in',
          })
          .data({
            windowTemplate: modal.windowTemplate,
          })
          .append(content);
        if (modal.windowClass) {
          angularDomEl.addClass(modal.windowClass);
        }

        if (modal.animation) {
          angularDomEl.attr('modal-animation', 'true');
        }

        appendToElement.addClass(modalBodyClass);
        if (modal.scope) {
          // we need to explicitly add the modal index to the modal scope
          // because it is needed by ngStyle to compute the zIndex property.
          modal.scope.$$topModalIndex = topModalIndex;
        }
        $animate.enter($compile(angularDomEl)(modal.scope), appendToElement);

        openedWindows.top().value.modalDomEl = angularDomEl;
        openedWindows.top().value.modalOpener = modalOpener;

        applyAriaHidden(angularDomEl);

        function applyAriaHidden(el) {
          if (!el || el[0].tagName === 'BODY') {
            return;
          }

          getSiblings(el).forEach(function (sibling) {
            const elemIsAlreadyHidden =
              sibling.getAttribute('aria-hidden') === 'true';
            let ariaHiddenCount = parseInt(
              sibling.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME),
              10,
            );

            if (!ariaHiddenCount) {
              ariaHiddenCount = elemIsAlreadyHidden ? 1 : 0;
            }

            sibling.setAttribute(
              ARIA_HIDDEN_ATTRIBUTE_NAME,
              ariaHiddenCount + 1,
            );
            sibling.setAttribute('aria-hidden', 'true');
          });

          return applyAriaHidden(el.parent());

          function getSiblings(el) {
            const children = el.parent() ? el.parent().children() : [];

            return Array.prototype.filter.call(children, function (child) {
              return child !== el[0];
            });
          }
        }
      };

      function broadcastClosing(modalWindow, resultOrReason, closing) {
        return !modalWindow.value.modalScope.$broadcast(
          'modal.closing',
          resultOrReason,
          closing,
        ).defaultPrevented;
      }

      function unhideBackgroundElements() {
        Array.prototype.forEach.call(
          document.querySelectorAll('[' + ARIA_HIDDEN_ATTRIBUTE_NAME + ']'),
          function (hiddenEl) {
            const ariaHiddenCount = parseInt(
              hiddenEl.getAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME),
              10,
            );
            let newHiddenCount = ariaHiddenCount - 1;
            hiddenEl.setAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME, newHiddenCount);

            if (!newHiddenCount) {
              hiddenEl.removeAttribute(ARIA_HIDDEN_ATTRIBUTE_NAME);
              hiddenEl.removeAttribute('aria-hidden');
            }
          },
        );
      }

      $modalStack.close = function (modalInstance, result) {
        let modalWindow = openedWindows.get(modalInstance);
        unhideBackgroundElements();
        if (modalWindow && broadcastClosing(modalWindow, result, true)) {
          modalWindow.value.modalScope.$$uibDestructionScheduled = true;
          modalWindow.value.deferred.resolve(result);
          removeModalWindow(modalInstance, modalWindow.value.modalOpener);
          return true;
        }

        return !modalWindow;
      };

      $modalStack.dismiss = function (modalInstance, reason) {
        let modalWindow = openedWindows.get(modalInstance);
        unhideBackgroundElements();
        if (modalWindow && broadcastClosing(modalWindow, reason, false)) {
          modalWindow.value.modalScope.$$uibDestructionScheduled = true;
          modalWindow.value.deferred.reject(reason);
          removeModalWindow(modalInstance, modalWindow.value.modalOpener);
          return true;
        }
        return !modalWindow;
      };

      $modalStack.dismissAll = function (reason) {
        let topModal = this.getTop();
        while (topModal && this.dismiss(topModal.key, reason)) {
          topModal = this.getTop();
        }
      };

      $modalStack.getElement = function (modalInstance) {
        const modalWindow = openedWindows.get(modalInstance);
        return modalWindow && modalWindow.value && modalWindow.value.modalDomEl;
      };

      $modalStack.getTop = function () {
        return openedWindows.top();
      };

      $modalStack.modalRendered = function (modalInstance) {
        const modalWindow = openedWindows.get(modalInstance);
        if (modalWindow) {
          modalWindow.value.renderDeferred.resolve();
        }
      };

      $modalStack.focusFirstFocusableElement = function (list) {
        if (list.length > 0) {
          list[0].focus();
          return true;
        }
        return false;
      };

      $modalStack.focusLastFocusableElement = function (list) {
        if (list.length > 0) {
          list[list.length - 1].focus();
          return true;
        }
        return false;
      };

      $modalStack.isModalFocused = function (evt, modalWindow) {
        if (evt && modalWindow) {
          const modalDomEl = modalWindow.value.modalDomEl;
          if (modalDomEl && modalDomEl.length) {
            return (evt.target || evt.srcElement) === modalDomEl[0];
          }
        }
        return false;
      };

      $modalStack.isFocusInFirstItem = function (evt, list) {
        if (list.length > 0) {
          return (evt.target || evt.srcElement) === list[0];
        }
        return false;
      };

      $modalStack.isFocusInLastItem = function (evt, list) {
        if (list.length > 0) {
          return (evt.target || evt.srcElement) === list[list.length - 1];
        }
        return false;
      };

      $modalStack.loadFocusElementList = function (modalWindow) {
        if (modalWindow) {
          const modalDomE1 = modalWindow.value.modalDomEl;
          if (modalDomE1 && modalDomE1.length) {
            const elements = modalDomE1[0].querySelectorAll(tabbableSelector);
            return elements
              ? Array.prototype.filter.call(elements, function (element) {
                  return isVisible(element);
                })
              : elements;
          }
        }
      };

      return $modalStack;
    },
  )

  .provider('$modal', function () {
    const $modalProvider = {
      options: {
        animation: true,
        backdrop: true, //can also be false or 'static'
        keyboard: true,
      },
      $get: /* @ngInject */ function (
        $rootScope,
        $q,
        $document,
        $templateRequest,
        $controller,
        $uibResolve,
        $modalStack,
        $log,
      ) {
        const $modal = {};

        function getTemplatePromise(options) {
          return options.template
            ? $q.when(options.template)
            : $templateRequest(
                angular.isFunction(options.templateUrl)
                  ? options.templateUrl()
                  : options.templateUrl,
              );
        }

        let promiseChain = null;
        $modal.getPromiseChain = function () {
          return promiseChain;
        };

        const openModals = {};

        /**
         * Makes it easy to create modals that should only ever open once
         * @param {String} key Unique identifier for this modal
         * @param {*} modalOptions Options to pass to $modal.open
         * @returns {*}
         */
        $modal.openOnce = function (key, modalOptions) {
          if (openModals[key]) {
            return openModals[key];
          } else {
            let modal = $modal.open(modalOptions);
            modal.result.finally(() => {
              delete openModals[key];
            });
            return (openModals[key] = modal);
          }
        };

        $modal.open = function (modalOptions) {
          if (
            modalOptions &&
            modalOptions.scope &&
            modalOptions.scope.$$destroyed
          ) {
            throw new Error('Tried to create modal on destroyed scope');
          }
          const modalResultDeferred = $q.defer();
          const modalOpenedDeferred = $q.defer();
          const modalClosedDeferred = $q.defer();
          const modalRenderDeferred = $q.defer();

          //prepare an instance of a modal to be injected into controllers and returned to a caller
          const modalInstance = {
            result: modalResultDeferred.promise,
            opened: modalOpenedDeferred.promise,
            closed: modalClosedDeferred.promise,
            rendered: modalRenderDeferred.promise,
            close: (result) => $modalStack.close(modalInstance, result),
            dismiss: (reason) => $modalStack.dismiss(modalInstance, reason),
            getElement: () => $modalStack.getElement(modalInstance),
          };

          //merge and clean up options
          modalOptions = angular.extend(
            {},
            $modalProvider.options,
            modalOptions,
          );
          modalOptions.resolve = modalOptions.resolve || {};
          modalOptions.appendTo =
            modalOptions.appendTo || $document.find('body').eq(0);

          if (!modalOptions.appendTo.length) {
            throw new Error(
              'appendTo element not found. Make sure that the element passed is in DOM.',
            );
          }

          //verify options
          if (
            !modalOptions.component &&
            !modalOptions.template &&
            !modalOptions.templateUrl
          ) {
            throw new Error(
              'One of component or template or templateUrl options is required.',
            );
          }

          let templateAndResolvePromise;
          if (modalOptions.component) {
            templateAndResolvePromise = $q.when(
              $uibResolve.resolve(modalOptions.resolve, {}, null, null),
            );
          } else {
            templateAndResolvePromise = $q.all([
              getTemplatePromise(modalOptions),
              $uibResolve.resolve(modalOptions.resolve, {}, null, null),
            ]);
          }

          function resolveWithTemplate() {
            return templateAndResolvePromise;
          }

          // Wait for the resolution of the existing promise chain.
          // Then switch to our own combined promise dependency (regardless of how the previous modal fared).
          // Then add to $modalStack and resolve opened.
          // Finally clean up the chain variable if no subsequent modal has overwritten it.
          let samePromise;
          samePromise = promiseChain = $q
            .all([promiseChain])
            .then(resolveWithTemplate, resolveWithTemplate)
            .then(function resolveSuccess(tplAndVars) {
              const providedScope = modalOptions.scope || $rootScope;

              const modalScope = providedScope.$new();
              modalScope.$close = modalInstance.close;
              modalScope.$dismiss = modalInstance.dismiss;

              /**
               * Can be used to dynamically prevent accidental dismissals. Pass in a function.
               * If the function returns a truthy value, then the modal will not be dismissable via
               * the backdrop or ESC
               * @param when {Function} A function called to see if we should cancel the modal dismissal
               */
              modalInstance.preventDismissWhen = function (when) {
                modalScope.$on('modal.closing', ($event, reason, closing) => {
                  if (
                    !closing &&
                    (reason === 'backdrop click' ||
                      reason === 'escape key press') &&
                    when()
                  ) {
                    $event.preventDefault();
                  }
                });
              };

              modalScope.$on('$destroy', function () {
                if (!modalScope.$$uibDestructionScheduled) {
                  modalScope.$dismiss('$uibUnscheduledDestruction');
                }
              });

              const modal = {
                scope: modalScope,
                deferred: modalResultDeferred,
                renderDeferred: modalRenderDeferred,
                closedDeferred: modalClosedDeferred,
                animation: modalOptions.animation,
                backdrop: modalOptions.backdrop,
                keyboard: modalOptions.keyboard,
                backdropClass: modalOptions.backdropClass,
                windowTopClass: modalOptions.windowTopClass,
                windowClass: modalOptions.windowClass,
                allowDarkMode: modalOptions.allowDarkMode,
                windowTemplate: modalOptions.windowTemplate,
                ariaLabelledBy: modalOptions.ariaLabelledBy,
                ariaDescribedBy: modalOptions.ariaDescribedBy,
                size: modalOptions.size,
                openedClass: modalOptions.openedClass,
                appendTo: modalOptions.appendTo,
              };

              const component = {};
              let ctrlInstance;
              let ctrlInstantiate;
              const ctrlLocals = {};

              if (modalOptions.component) {
                constructLocals(component, false, true, false);
                component.name = modalOptions.component;
                modal.component = component;
              } else if (modalOptions.controller) {
                constructLocals(ctrlLocals, true, false, true);

                // the third param will make the controller instantiate later,private api
                // @see https://github.com/angular/angular.js/blob/master/src/ng/controller.js#L126
                ctrlInstantiate = $controller(
                  modalOptions.controller,
                  ctrlLocals,
                  true,
                  modalOptions.controllerAs,
                );
                if (
                  modalOptions.controllerAs &&
                  modalOptions.bindToController
                ) {
                  ctrlInstance = ctrlInstantiate.instance;
                  ctrlInstance.$close = modalScope.$close;
                  ctrlInstance.$dismiss = modalScope.$dismiss;
                  angular.extend(
                    ctrlInstance,
                    {
                      $resolve: ctrlLocals.$scope.$resolve,
                    },
                    providedScope,
                  );
                }

                ctrlInstance = ctrlInstantiate();

                if (angular.isFunction(ctrlInstance.$onInit)) {
                  ctrlInstance.$onInit();
                }
              }

              if (!modalOptions.component) {
                modal.content = tplAndVars[0];
              }

              $modalStack.open(modalInstance, modal);
              modalOpenedDeferred.resolve(true);

              function constructLocals(
                obj,
                template,
                instanceOnScope,
                injectable,
              ) {
                obj.$scope = modalScope;
                obj.$scope.$resolve = {};
                if (instanceOnScope) {
                  obj.$scope.$modalInstance = modalInstance;
                } else {
                  obj.$modalInstance = modalInstance;
                }

                const resolves = template ? tplAndVars[1] : tplAndVars;
                angular.forEach(resolves, function (value, key) {
                  if (injectable) {
                    obj[key] = value;
                  }

                  obj.$scope.$resolve[key] = value;
                });
              }
            })
            .catch(function resolveError(reason) {
              $log.error('Unable to open modal', reason);
              modalOpenedDeferred.reject(reason);
              modalResultDeferred.reject(reason);
            })
            .finally(function () {
              if (promiseChain === samePromise) {
                promiseChain = null;
              }
            });

          return modalInstance;
        };

        return $modal;
      },
    };

    return $modalProvider;
  })

  // Simple directive that can be used to render a sliding wizard inside a dialog
  .directive('modalWizard', function ($animate) {
    return {
      restrict: 'E',
      link(scope, $element) {
        const element = $element[0];
        const els = {};

        function checkEl(type) {
          return function ($animElement, phase) {
            let animElement = $animElement[0];
            if (animElement.parentNode === element) {
              let isStart = phase === 'start';
              $element.toggleClass('active', isStart);
              els[type] = isStart && animElement;
              // determine if the entering node is before or after the leaving node
              let reverse =
                els.enter &&
                els.leave &&
                els.enter.compareDocumentPosition(els.leave) &
                  Node.DOCUMENT_POSITION_FOLLOWING;
              $element.toggleClass('reverse', !!reverse);
            }
          };
        }

        $animate.on('enter', $element, checkEl('enter'));
        $animate.on('leave', $element, checkEl('leave'));
      },
    };
  });
